import React, { useState, useEffect } from "react";
import { z } from "zod";
import { useUserContext } from "../config/UserContext";
import { useKeycloak } from "../config/KeycloakContext";
import { DOCUMENT_TYPE } from '../../../shared/types/models/document';
import type { Parcel } from "../../../shared/types/models/parcel";

interface SimplePropertyAddressCardProps {
  onContinue: () => void;
  documentType?: number;
}

const SimplePropertyAddressCard: React.FC<SimplePropertyAddressCardProps> = ({ onContinue, documentType }) => {
  const { state, county, apiBaseUrl, freeze, parcels, setParcels } = useUserContext();
  const { getToken } = useKeycloak();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  
  const initialParcel = parcels && parcels.length > 0 ? parcels[0] : {};
  const [parcelData, setParcelData] = useState<Partial<Parcel>>(initialParcel);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const isLienRelease = documentType !== DOCUMENT_TYPE.LIEN;

  const parsedSchema = typeof county?.documentSchema === 'string'
    ? (() => { try { return JSON.parse(county.documentSchema); } catch { return null; } })()
    : county?.documentSchema;

  const parcelSchema = parsedSchema?.parcelSchema || {
    requiresAddress: true,
    requiresLegalDescription: false,
    requiresAbbreviatedLegalDescription: false,
    parcelIndexes: []
  };

  const getDynamicSchema = () => {
    let schemaObj: Record<string, any> = {};

    if (parcelSchema.requiresAddress) {
      schemaObj.street1 = z.string().min(1, "Address is required");
      schemaObj.city = z.string().min(1, "City is required");
      schemaObj.zipcode = z.string().min(1, "Zip code is required").regex(/^[0-9]{5}(-[0-9]{4})?$/, "Valid ZIP required");
    }

    if (parcelSchema.requiresLegalDescription) {
      schemaObj.legalDescription = z.string().min(1, "Legal Description is required");
    }

    if (parcelSchema.parcelIndexes) {
      parcelSchema.parcelIndexes.forEach((idx: any) => {
        const isReq = idx.required !== false && String(idx.required).toLowerCase() !== "false" && idx.required !== 0;
        if (isReq) {
          schemaObj[`index_${idx.key}`] = z.string().min(1, `${idx.label} is required`);
        } else {
          schemaObj[`index_${idx.key}`] = z.string().optional();
        }
      });
    }

    return z.object(schemaObj);
  };

  const schema = getDynamicSchema();

  const getFormData = () => {
    const data: Record<string, any> = {
      street1: parcelData.street1,
      city: parcelData.city,
      zipcode: parcelData.zipcode,
      legalDescription: parcelData.legalDescription
    };
    if (parcelSchema.parcelIndexes) {
      parcelSchema.parcelIndexes.forEach((idx: any) => {
        data[`index_${idx.key}`] = parcelData.recordingIndexes?.[idx.key];
      });
    }
    return data;
  };

  const formData = getFormData();
  const isFormValid = schema.safeParse(formData).success;

  const handleBlur = (field: string, value: any) => {
    setTouched(t => ({ ...t, [field]: true }));
    const fieldSchema = schema.shape[field];
    if (fieldSchema) {
      const result = fieldSchema.safeParse(value);
      if (!result.success) {
        setErrors(e => ({ ...e, [field]: result.error.errors[0].message }));
      } else {
        setErrors(e => {
          const newE = { ...e };
          delete newE[field];
          return newE;
        });
      }
    }
  };

  const handleChange = (field: string, value: string) => {
    if (field.startsWith('index_')) {
      const key = field.replace('index_', '');
      setParcelData(prev => ({
        ...prev,
        recordingIndexes: {
          ...(prev.recordingIndexes || {}),
          [key]: value
        }
      }));
    } else {
      setParcelData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLienRelease) {
      onContinue();
      return;
    }

    const currentFormData = getFormData();
    
    // Touch all fields
    const newTouched: Record<string, boolean> = {};
    Object.keys(currentFormData).forEach(k => newTouched[k] = true);
    setTouched(newTouched);

    const result = schema.safeParse(currentFormData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");
      if (!freeze) throw new Error("Freeze not found");

      const payload = {
        ...parcelData,
        stateId: state?.id,
        countyId: county?.id,
      };

      const res = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${freeze.lookup}/parcels`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ parcels: [payload] }),
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to save property info");
      }
      const data = await res.json();
      setParcels(data.data);
      setSaving(false);
      onContinue();
    } catch (err: unknown) {
      if (err instanceof Error) {
        setSaveError(err.message || "Failed to save property info");
      } else {
        setSaveError("Failed to save property info");
      }
      setSaving(false);
    }
  };

  return (
    <form className="w-full flex flex-col gap-4" onSubmit={handleSubmit} autoComplete="off">
      
      {parcelSchema.parcelIndexes && parcelSchema.parcelIndexes.map((idx: any) => (
        <div key={idx.key} className="flex flex-col gap-1 w-full">
          <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta">
            {idx.label} {idx.required !== false && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            className={`w-full px-3 py-2 rounded border ${errors[`index_${idx.key}`] && touched[`index_${idx.key}`] ? 'border-red-500' : 'border-[#D7DFEC]'} bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none`}
            value={parcelData.recordingIndexes?.[idx.key] || ''}
            onChange={e => handleChange(`index_${idx.key}`, e.target.value)}
            onBlur={e => handleBlur(`index_${idx.key}`, e.target.value)}
            disabled={isLienRelease}
          />
          {errors[`index_${idx.key}`] && touched[`index_${idx.key}`] && <span className="text-red-500 text-xs">{errors[`index_${idx.key}`]}</span>}
        </div>
      ))}

      {parcelSchema.requiresAddress && (
        <>
          <div className="flex flex-col gap-1 w-full mt-2">
            <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta">Address</label>
            <input
              type="text"
              className={`w-full px-3 py-2 rounded border ${errors.street1 && touched.street1 ? 'border-red-500' : 'border-[#D7DFEC]'} bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none`}
              value={parcelData.street1 || ''}
              onChange={e => handleChange('street1', e.target.value)}
              onBlur={e => handleBlur('street1', e.target.value)}
              disabled={isLienRelease}
            />
            {errors.street1 && touched.street1 && <span className="text-red-500 text-xs">{errors.street1}</span>}
          </div>

          <div className="flex flex-col gap-1 w-full">
            <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta">Address 2</label>
            <input
              type="text"
              className="w-full px-3 py-2 rounded border border-[#D7DFEC] bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none"
              value={parcelData.street2 || ''}
              onChange={e => handleChange('street2', e.target.value)}
              disabled={isLienRelease}
            />
          </div>

          <div className="flex flex-row gap-3 w-full">
            <div className="flex flex-col gap-1 w-1/2">
              <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta">City</label>
              <input
                type="text"
                className={`w-full px-3 py-2 rounded border ${errors.city && touched.city ? 'border-red-500' : 'border-[#D7DFEC]'} bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none`}
                value={parcelData.city || ''}
                onChange={e => handleChange('city', e.target.value)}
                onBlur={e => handleBlur('city', e.target.value)}
                disabled={isLienRelease}
              />
              {errors.city && touched.city && <span className="text-red-500 text-xs">{errors.city}</span>}
            </div>
            <div className="flex flex-col gap-1 w-1/2">
              <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta">Zip code</label>
              <input
                type="text"
                className={`w-full px-3 py-2 rounded border ${errors.zipcode && touched.zipcode ? 'border-red-500' : 'border-[#D7DFEC]'} bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none`}
                value={parcelData.zipcode || ''}
                onChange={e => handleChange('zipcode', e.target.value)}
                onBlur={e => handleBlur('zipcode', e.target.value)}
                disabled={isLienRelease}
              />
              {errors.zipcode && touched.zipcode && <span className="text-red-500 text-xs">{errors.zipcode}</span>}
            </div>
          </div>
        </>
      )}

      {parcelSchema.requiresLegalDescription && (
        <div className="flex flex-col gap-1 w-full mt-2">
          <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta">Legal Description</label>
          <textarea
            className={`w-full px-3 py-2 rounded border ${errors.legalDescription && touched.legalDescription ? 'border-red-500' : 'border-[#D7DFEC]'} bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none min-h-[100px]`}
            value={parcelData.legalDescription || ''}
            onChange={e => handleChange('legalDescription', e.target.value)}
            onBlur={e => handleBlur('legalDescription', e.target.value)}
            disabled={isLienRelease}
          />
          {errors.legalDescription && touched.legalDescription && <span className="text-red-500 text-xs">{errors.legalDescription}</span>}
        </div>
      )}

      <button
        type="submit"
        className={`w-full bg-[#0B4BB6] text-white font-bold text-[15px] font-plus-jakarta py-2.5 rounded-full mt-4 hover:bg-[#23235a] transition-colors border-2 border-[#0B4BB6] ${(!isFormValid || saving) && !isLienRelease ? 'opacity-60 cursor-not-allowed' : ''}`}
        disabled={isLienRelease ? saving : (!isFormValid || saving)}
      >
        {saving ? 'Saving...' : 'Save & Continue'}
      </button>
      {saveError && <div className="text-red-500 mt-2">{saveError}</div>}
    </form>
  );
};

export default SimplePropertyAddressCard;
